import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useLoginCheck } from "../../../../hooks/useLoginCheck";
import ScheduleFormDrawer from "./ScheduleFormDrawer";
import "../../../../styles/scss/recital2027RehearsalSchedule.scss";

/**
 * 発表会2027（SLASH GALLERY vol.5）リハーサル日程ページ。
 *
 * 設計方針:
 * - recital2027_schedules(manager_v2_DB) の表示専用。日程の登録・変更は slash-app 側で行う。
 * - 種別は 地方選抜(regional) / HEADZ(headz) / 特別ナンバー(special) の3つ。特別ナンバーは
 *   作品が紐づく行なので、カレンダーにも一覧にも「作品タイトル」を出す（会場は従属表示）。
 * - 既定は「今月」のカレンダー。月移動は予定のある月と今月を含む連続した月を行き来する
 *   （予定ゼロの月も空のカレンダーとして開ける）。
 * - 終わった日程は薄く表示して記録として残す。会場未確保(is_booked=false)は破線＋バッジで示す。
 * - 一覧は枠内スクロールにして、開いた時点で「今日以降」の位置まで送っておく（過ぎた日程は
 *   普段見ないため）。上へスクロールすれば過去も遡れる。
 * - 日付セルをクリックするとその日の全予定（同日に選抜と特別ナンバーが重なる日も含めて）を
 *   右のパネルに出す。そこから編集・削除・その日への追加ができる。
 * - 予定の無いマスにマウスを乗せると「＋」が出て、その日付で追加フォームが開く。
 * - 削除は確認を挟む。実体は delete_flg を立てる論理削除で、行はDBに残る。
 */

const apiBase = process.env.REACT_APP_API_URL;

const DOW = ["日", "月", "火", "水", "木", "金", "土"];

// 地方選抜はエリアごとに色を変える。HEADZ・特別ナンバーは種別そのものの色。
const AREA_COLOR = {
  大阪: "var(--c-osaka)",
  兵庫: "var(--c-hyogo)",
  京都: "var(--c-kyoto)",
  奈良: "var(--c-nara)",
};

// 特別ナンバーは special_work_id に作品が紐づく行として入る。kind の綴りに依存せず
// 「作品が紐づいていれば特別ナンバー」で判定する（API 側と同じ規則）。
const kindOf = (s) =>
  s.special_work_id != null || s.kind === "special" ? "special" : s.kind;

const toneOf = (s) => {
  const k = kindOf(s);
  if (k === "headz") return "var(--c-headz)";
  if (k === "special") return "var(--c-special)";
  return AREA_COLOR[s.regional_area || s.venue_area] || "var(--accent)";
};

const kindLabel = (s) => {
  const k = kindOf(s);
  if (k === "headz") return "HEADZ";
  if (k === "special") return "特別ナンバー";
  return `地方選抜 ${s.regional_area || ""}`.trim();
};

// カレンダーのセルは幅が狭いので短いラベル（HEADZ / 特別 / エリア名）にする。
const kindShort = (s) => {
  const k = kindOf(s);
  if (k === "headz") return "HEADZ";
  if (k === "special") return "特別";
  return s.regional_area || s.venue_area || "";
};

// セルの2行目: 特別ナンバーは作品タイトル、選抜/HEADZは会場名。
const subLabel = (s) =>
  kindOf(s) === "special"
    ? s.special_title || "（タイトル未入力）"
    : s.studio || "会場未定";

const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;

const addMonth = (ym, n) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const today = () => ymd(new Date());

// 地方選抜はエリアごとに絞り込めるよう、エリア名をそのまま value にする。
const REGIONAL_AREAS = ["大阪", "京都", "兵庫", "奈良"];

const KIND_FILTERS = [
  { value: "all", label: "すべて", tone: "" },
  ...REGIONAL_AREAS.map((a) => ({
    value: a,
    label: a,
    tone: "area",
    color: AREA_COLOR[a],
  })),
  { value: "headz", label: "HEADZ", tone: "headz" },
  { value: "special", label: "特別ナンバー", tone: "special" },
];

const BOOKED_FILTERS = [
  { value: "all", label: "すべて" },
  { value: "1", label: "予約済" },
  { value: "0", label: "未予約" },
];

const LEGEND = [
  { color: "var(--c-osaka)", label: "大阪" },
  { color: "var(--c-hyogo)", label: "兵庫" },
  { color: "var(--c-kyoto)", label: "京都" },
  { color: "var(--c-nara)", label: "奈良" },
  { color: "var(--c-headz)", label: "HEADZ" },
  { color: "var(--c-special)", label: "特別ナンバー" },
];

function RehearsalSchedulePage() {
  useLoginCheck();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("cal");
  const [kind, setKind] = useState("all");
  const [booked, setBooked] = useState("all");
  const [month, setMonth] = useState(() => today().slice(0, 7));
  const [drawerDate, setDrawerDate] = useState(null);
  const [works, setWorks] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [formTarget, setFormTarget] = useState(null); // 編集対象。null なら新規
  const [formDate, setFormDate] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const listRef = useRef(null);

  // 今日はレンダー中に固定する（日付をまたいでも表示がぶれないよう1回だけ評価）。
  const [TODAY] = useState(today);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${apiBase}/api/get_recital2027_schedules/`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setWorks(Array.isArray(data.works) ? data.works : []);
    } catch (e) {
      console.error("リハーサル日程の取得に失敗しました:", e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const matches = useCallback(
    (s) => {
      if (REGIONAL_AREAS.includes(kind)) {
        if (kindOf(s) !== "regional" || s.regional_area !== kind) return false;
      } else if (kind !== "all" && kindOf(s) !== kind) return false;
      if (booked !== "all" && String(s.is_booked) !== booked) return false;
      return true;
    },
    [kind, booked],
  );

  const visible = useMemo(() => rows.filter(matches), [rows, matches]);

  // 月の移動範囲。既定は今月で、予定のある月と今月を含む連続した月を行き来する。
  const months = useMemo(() => {
    const thisMonth = TODAY.slice(0, 7);
    const dataMonths = [...new Set(rows.map((s) => s.date.slice(0, 7)))].sort();
    if (dataMonths.length === 0) return [thisMonth];
    const start = [dataMonths[0], thisMonth].sort()[0];
    const end = [dataMonths[dataMonths.length - 1], thisMonth].sort().pop();
    const out = [];
    let m = start;
    while (m <= end) {
      out.push(m);
      m = addMonth(m, 1);
    }
    return out;
  }, [rows, TODAY]);

  // 取得後、今月が範囲外（過去の発表会を見ているなど）なら先頭の月へ寄せる。
  useEffect(() => {
    if (months.length && !months.includes(month)) setMonth(months[0]);
  }, [months, month]);

  const byDate = useMemo(() => {
    const map = {};
    rows.forEach((s) => {
      if (!map[s.date]) map[s.date] = [];
      map[s.date].push(s);
    });
    Object.keys(map).forEach((k) => {
      map[k] = [...map[k]].sort((a, b) =>
        (a.start || "").localeCompare(b.start || ""),
      );
    });
    return map;
  }, [rows]);

  // カレンダーのセル（前後の月にはみ出す分を含む6週。最終週が全部翌月なら5週で切る）。
  const cells = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const first = new Date(y, m - 1, 1);
    const start = new Date(first);
    start.setDate(1 - first.getDay());
    const out = [];
    for (let i = 0; i < 42; i += 1) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const key = ymd(d);
      const out_of_month = d.getMonth() !== m - 1;
      if (i >= 35 && out_of_month) break;
      out.push({
        key,
        day: d.getDate(),
        dow: d.getDay(),
        outOfMonth: out_of_month,
        isPast: key < TODAY,
        isToday: key === TODAY,
        events: byDate[key] || [],
      });
    }
    return out;
  }, [month, byDate, TODAY]);

  const monthCount = useMemo(
    () => visible.filter((s) => s.date.startsWith(month)).length,
    [visible, month],
  );

  const listRows = useMemo(
    () =>
      [...visible].sort((a, b) =>
        `${a.date}${a.start}`.localeCompare(`${b.date}${b.start}`),
      ),
    [visible],
  );

  // 一覧に「ここから先が今日以降」の区切りを1本だけ入れる。
  const firstFutureId = useMemo(() => {
    const f = listRows.find((s) => s.date >= TODAY);
    return f && listRows[0] !== f ? f.id : null;
  }, [listRows, TODAY]);

  // 一覧は過ぎた日程が上に積み上がるので、開いた時点で「今日以降」の位置まで送っておく。
  // ページ全体は動かさず、一覧の枠の中だけをスクロールさせる。
  useEffect(() => {
    if (view !== "list") return;
    const container = listRef.current;
    if (!container) return;
    const head = container.querySelector('[data-future-head="1"]');
    // .list は position:relative なので、中の要素の offsetTop はこの枠の内側の座標。
    // そのまま scrollTop に入れると「今日以降」の区切りが枠の一番上に来る。
    container.scrollTop = head ? Math.max(0, head.offsetTop - 8) : 0;
  }, [view, listRows, firstFutureId]);

  const openCreate = useCallback((date) => {
    setFormTarget(null);
    setFormDate(date || "");
    setDrawerDate(null);
    setFormOpen(true);
  }, []);

  const openEdit = useCallback((row) => {
    setFormTarget(row);
    setFormDate("");
    setDrawerDate(null);
    setFormOpen(true);
  }, []);

  const handleSaved = useCallback(() => {
    setFormOpen(false);
    fetchData();
  }, [fetchData]);

  // 削除は確認を挟む。実体は delete_flg を立てるだけなので行はDBに残る。
  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      const res = await fetch(`${apiBase}/api/delete_recital2027_schedule/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: deleteTarget.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status === "error") {
        throw new Error(data.message || `HTTP ${res.status}`);
      }
      setDeleteTarget(null);
      setDrawerDate(null);
      fetchData();
    } catch (e) {
      console.error("リハーサル日程の削除に失敗しました:", e);
      alert(`削除に失敗しました: ${e.message}`);
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, fetchData]);

  // 上のバッジは「全体の内訳」なので、絞り込みでは動かさない（rows で数える）。
  // 絞り込みの効き具合はカレンダー見出しの「◯件」と一覧の行数で分かる。
  const badges = useMemo(
    () => [
      { key: "all", cls: "", label: "予定", n: rows.length },
      {
        key: "regional",
        cls: "regional",
        label: "地方選抜",
        n: rows.filter((s) => kindOf(s) === "regional").length,
      },
      {
        key: "headz",
        cls: "headz",
        label: "HEADZ",
        n: rows.filter((s) => kindOf(s) === "headz").length,
      },
      {
        key: "special",
        cls: "special",
        label: "特別ナンバー",
        n: rows.filter((s) => kindOf(s) === "special").length,
      },
      {
        key: "unbooked",
        cls: "unbooked",
        label: "未予約",
        n: rows.filter((s) => !s.is_booked).length,
      },
    ],
    [rows],
  );

  const monthIndex = months.indexOf(month);
  const drawerEvents = drawerDate ? byDate[drawerDate] || [] : [];

  // Esc で詳細を閉じる
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setDeleteTarget(null);
      setFormOpen(false);
      setDrawerDate(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const drawerTitle = useMemo(() => {
    if (!drawerDate) return "";
    const d = new Date(`${drawerDate}T00:00:00`);
    return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日（${
      DOW[d.getDay()]
    }）`;
  }, [drawerDate]);

  return (
    <div className="recital2027-rehearsal-schedule-page">
      {/* header */}
      <div className="page-header">
        <div className="header-left">
          <p className="eyebrow">Recital 2027 / Rehearsal</p>
          <h1>リハーサル日程</h1>
          <p className="sub">
            地方選抜・HEADZ・特別ナンバーのリハーサル予定
          </p>
        </div>
        <div className="header-right">
          <div className="view-toggle">
            <button
              type="button"
              className={view === "cal" ? "active" : ""}
              onClick={() => setView("cal")}
            >
              カレンダー
            </button>
            <button
              type="button"
              className={view === "list" ? "active" : ""}
              onClick={() => setView("list")}
            >
              一覧
            </button>
          </div>
          <button
            type="button"
            className="btn primary"
            onClick={() => openCreate("")}
          >
            ＋ 日程を追加
          </button>
        </div>
      </div>

      {/* summary */}
      <div className="sum-badges">
        {badges.map((b) => (
          <span key={b.key} className={`sum-badge ${b.cls}`}>
            <span className="sb-label">{b.label}</span>
            <span className="sb-num">{b.n}</span>
          </span>
        ))}
      </div>

      {/* filters */}
      <div className="panel">
        <div className="filters">
          <div className="fgroup">
            <span className="fcap">種別</span>
            {KIND_FILTERS.map((f) => (
              <button
                type="button"
                key={f.value}
                data-tone={f.tone}
                style={f.color ? { "--tone": f.color } : undefined}
                className={`chip ${kind === f.value ? "active" : ""}`}
                onClick={() => setKind(f.value)}
              >
                {f.tone && <span className="dot" />}
                {f.label}
              </button>
            ))}
          </div>
          <div className="fgroup">
            <span className="fcap">予約</span>
            {BOOKED_FILTERS.map((f) => (
              <button
                type="button"
                key={f.value}
                className={`chip ${booked === f.value ? "active" : ""}`}
                onClick={() => setBooked(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* calendar */}
      {view === "cal" && (
        <div className="panel">
          <div className="cal-head">
            <div className="month-nav">
              <button
                type="button"
                className="nav-btn"
                onClick={() => setMonth(months[monthIndex - 1])}
                disabled={monthIndex <= 0}
              >
                ‹
              </button>
              <div className="month-label">
                {month.split("-")[0]}年{Number(month.split("-")[1])}月
              </div>
              <button
                type="button"
                className="nav-btn"
                onClick={() => setMonth(months[monthIndex + 1])}
                disabled={monthIndex >= months.length - 1}
              >
                ›
              </button>
              <span className="month-count">{monthCount}件</span>
            </div>
            <div className="legend">
              {LEGEND.map((l) => (
                <span key={l.label}>
                  <i style={{ background: l.color }} />
                  {l.label}
                </span>
              ))}
              <span>
                <i className="dashed" />
                未予約
              </span>
            </div>
          </div>

          {!loading && monthCount === 0 && (
            <div className="no-plan">この月のリハーサル予定はありません</div>
          )}

          <div className="grid">
            {DOW.map((d, i) => (
              <div
                key={d}
                className={`dow ${i === 0 ? "sun" : ""} ${i === 6 ? "sat" : ""}`}
              >
                {d}
              </div>
            ))}
          </div>
          <div className="grid">
            {cells.map((c) => {
              // 予定の無い当月のマスは、押すとその日付で追加フォームが開く
              const addable = !c.outOfMonth && c.events.length === 0;
              return (
              <div
                key={c.key}
                className={`cell ${c.outOfMonth ? "out" : ""} ${
                  c.isPast ? "past" : ""
                } ${c.dow === 0 ? "sun" : ""} ${c.dow === 6 ? "sat" : ""} ${
                  c.isToday ? "today" : ""
                } ${c.events.length ? "has-ev" : ""} ${addable ? "addable" : ""}`}
                onClick={
                  c.events.length
                    ? () => setDrawerDate(c.key)
                    : addable
                      ? () => openCreate(c.key)
                      : undefined
                }
              >
                <span className="dnum">{c.day}</span>
                {addable && <span className="cell-add">＋</span>}
                <div className="evs">
                  {c.events.map((s) => (
                    <div
                      key={s.id}
                      className={`ev ${s.is_booked ? "" : "unbooked"} ${
                        c.isPast ? "past" : ""
                      } ${matches(s) ? "" : "dim"}`}
                      style={{ "--tone": toneOf(s) }}
                    >
                      <span className="et">
                        {s.start} {kindShort(s)}
                        {s.note ? " ※" : ""}
                      </span>
                      <span className="en">{subLabel(s)}</span>
                    </div>
                  ))}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* list */}
      {view === "list" && (
        <div className="panel">
          <div className="list" ref={listRef}>
            {listRows.length === 0 && (
              <div className="empty">
                {loading ? "読み込み中…" : "条件に合う予定がありません"}
              </div>
            )}
            {listRows.map((s) => {
              const d = new Date(`${s.date}T00:00:00`);
              return (
                <React.Fragment key={s.id}>
                  {s.id === firstFutureId && (
                    <div className="today-sep" data-future-head="1">
                      <span>今日以降</span>
                      <i />
                    </div>
                  )}
                  <div
                    className={`lrow ${s.date < TODAY ? "past" : ""}`}
                    style={{ "--tone": toneOf(s) }}
                    onClick={() => setDrawerDate(s.date)}
                  >
                    <div className="l-date">
                      {d.getMonth() + 1}/{d.getDate()}
                      <small>
                        {d.getFullYear()}年 ({DOW[d.getDay()]})
                      </small>
                    </div>
                    <div className="l-kind">
                      {kindLabel(s)}
                      {s.note ? " ※" : ""}
                    </div>
                    <div className="l-time">
                      {s.start || "--:--"} – {s.end || "--:--"}
                    </div>
                    <div className="l-place">
                      {kindOf(s) === "special" ? (
                        <>
                          {s.special_title || "（タイトル未入力）"}
                          <small>{s.studio || "会場未定"}</small>
                        </>
                      ) : (
                        <>
                          {s.studio || "会場未定"}
                          <small>{s.venue_area || ""}</small>
                        </>
                      )}
                    </div>
                    <div className="l-status">
                      <span className={`tag ${s.is_booked ? "ok" : "ng"}`}>
                        {s.is_booked ? "予約済" : "未予約"}
                      </span>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      {/* 日付の詳細 */}
      <div
        className={`sch-mask ${drawerDate || formOpen ? "show" : ""}`}
        onClick={() => {
          setDrawerDate(null);
          setFormOpen(false);
        }}
      />
      <aside className={`sch-drawer ${drawerDate ? "show" : ""}`}>
        <div className="dhead">
          <h2>{drawerTitle}</h2>
          <button
            type="button"
            className="close"
            onClick={() => setDrawerDate(null)}
          >
            ×
          </button>
        </div>
        <div className="dbody">
          {drawerEvents.map((x) => (
            <div key={x.id} className="dcard" style={{ "--tone": toneOf(x) }}>
              <div className="kind">{kindLabel(x)}</div>
              {kindOf(x) === "special" && (
                <div className="drow">
                  <span className="k">作品</span>
                  <span>{x.special_title || "（タイトル未入力）"}</span>
                </div>
              )}
              <div className="drow">
                <span className="k">時間</span>
                <span>
                  {x.start || "--:--"} – {x.end || "--:--"}
                </span>
              </div>
              <div className="drow">
                <span className="k">会場</span>
                <span>
                  {x.studio || "会場未定"}
                  {x.venue_area ? `（${x.venue_area}）` : ""}
                </span>
              </div>
              <div className="drow">
                <span className="k">予約</span>
                <span className={`tag ${x.is_booked ? "ok" : "ng"}`}>
                  {x.is_booked ? "予約済" : "未予約"}
                </span>
              </div>
              {x.note && <div className="note">{x.note}</div>}
              <div className="dcard-actions">
                <button
                  type="button"
                  className="btn sm"
                  onClick={() => openEdit(x)}
                >
                  ✎ 編集
                </button>
                <button
                  type="button"
                  className="btn sm danger"
                  onClick={() => setDeleteTarget(x)}
                >
                  🗑 削除
                </button>
              </div>
            </div>
          ))}
          {drawerDate && (
            <button
              type="button"
              className="btn primary"
              onClick={() => openCreate(drawerDate)}
            >
              ＋ この日に追加
            </button>
          )}
        </div>
      </aside>

      <ScheduleFormDrawer
        open={formOpen}
        target={formTarget}
        defaultDate={formDate}
        works={works}
        onClose={() => setFormOpen(false)}
        onSaved={handleSaved}
      />

      {/* 削除の確認。実体は論理削除なので、消しても行はDBに残る。 */}
      <div className={`confirm ${deleteTarget ? "show" : ""}`}>
        <div className="confirm-box">
          <h3>この日程を削除しますか？</h3>
          <p>
            削除すると一覧・カレンダーから消えます。
            <br />
            データは残るので、必要なら復元できます。
          </p>
          {deleteTarget && (
            <div
              className="target"
              style={{ "--tone": toneOf(deleteTarget) }}
            >
              {(() => {
                const d = new Date(`${deleteTarget.date}T00:00:00`);
                return `${d.getMonth() + 1}/${d.getDate()}（${
                  DOW[d.getDay()]
                }）${deleteTarget.start || ""}–${deleteTarget.end || ""}`;
              })()}
              <br />
              {kindLabel(deleteTarget)}
              {kindOf(deleteTarget) === "special" && deleteTarget.special_title
                ? ` / ${deleteTarget.special_title}`
                : ""}
              　{deleteTarget.studio || "会場未定"}
            </div>
          )}
          <div className="f-actions">
            <button
              type="button"
              className="btn"
              onClick={() => setDeleteTarget(null)}
            >
              キャンセル
            </button>
            <button
              type="button"
              className="btn danger"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? "削除中…" : "削除する"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RehearsalSchedulePage;
